import { motion } from "framer-motion";
import { Fingerprint, Megaphone, MonitorSmartphone, Package } from "lucide-react";
import { ChapterHead } from "./AboutSection";
import { RingFull } from "./GeoShapes";

const SERVICES = [
  {
    code: "SRV_01",
    name: "Brand Identity per il Food & Beverage",
    tag: "Core Expertise",
    desc: "Posizionamento, naming, marchi e linee guida complete: la base visiva che rende un prodotto riconoscibile e memorabile nel suo mercato.",
    deliverables: ["Brand Strategy", "Logo & Marchio", "Palette & Tipografia", "Brand Guideline"],
    accent: "#00C2E8",
    Icon: Fingerprint,
  },
  {
    code: "SRV_02",
    name: "Packaging & Label Design",
    tag: "Dallo scaffale all'e-commerce",
    desc: "Etichette per vini, prosecco e prodotti d'eccellenza, packaging alimentare progettato per valorizzare il prodotto e dominare lo scaffale.",
    deliverables: ["Etichette fronte/retro", "Fustelle esecutive", "Finiture & carte speciali", "Render 3D prodotto"],
    accent: "#E200C8",
    Icon: Package,
  },
  {
    code: "SRV_03",
    name: "Touchpoint Digitali",
    tag: "Esperienze digitali",
    desc: "Siti, landing page, kit social e visual per e-commerce: lo stesso sistema visivo del brand, applicato con coerenza anche online.",
    deliverables: ["Sito & Landing page", "Kit Social", "Visual e-commerce", "Design System"],
    accent: "#2020E0",
    Icon: MonitorSmartphone,
  },
  {
    code: "SRV_04",
    name: "Comunicazione & Campagne",
    tag: "Ogni contesto di marca",
    desc: "Poster, materiali per fiere ed eventi, menu e campagne adv: ogni punto di contatto racconta lo stesso brand, con la stessa voce.",
    deliverables: ["Poster & Outdoor ADV", "Materiali per fiere", "Menu & Listini", "Merchandising"],
    accent: "#0D0E11",
    Icon: Megaphone,
  },
];

// Anteprime fotografiche dal portfolio Behance, una per categoria di servizio
const SERVICE_IMAGES = {
  SRV_01: { src: "/assets/p-alfieri.png", alt: "Branding — Alfieri Pasticceria d'Autore" },
  SRV_02: { src: "/assets/f-rosamore.jpg", alt: "Label — ROSAMORE Prosecco Doc" },
  SRV_03: { src: "/assets/p-flixbus.png", alt: "UI/UX — FLIXBUS Redesign App" },
  SRV_04: { src: "/assets/p-monreve.png", alt: "Poster — Mon Rêve Summer Festival" },
};

export default function ServicesSection() {
  return (
    <section id="servizi" data-testid="services-section" className="relative bg-white">
      <RingFull className="pointer-events-none absolute right-6 top-24 hidden h-16 w-16 lg:block" />
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
        <ChapterHead number="02" title="Servizi per il tuo brand" testid="services-heading" />

        <div className="border-t border-neutral-200">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative grid grid-cols-1 gap-6 overflow-hidden border-b border-neutral-200 py-10 transition-colors duration-500 hover:bg-paper lg:grid-cols-12 lg:items-center lg:gap-4"
              data-testid={`service-row-${s.code.toLowerCase()}`}
            >
              <div
                className="pointer-events-none absolute -right-12 top-1/2 h-36 w-36 -translate-y-1/2 rounded-full border-[20px] opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                style={{ borderColor: s.accent }}
              />
              <div className="lg:col-span-2">
                <span
                  data-testid={`service-icon-${s.code.toLowerCase()}`}
                  className="mb-4 flex h-12 w-12 items-center justify-center border border-neutral-200 text-ink transition-all duration-500 group-hover:border-transparent group-hover:text-paper"
                  style={{ backgroundColor: "transparent" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = s.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <s.Icon className="h-5 w-5" />
                </span>
                <span className="font-mono text-xs font-bold tracking-[0.2em]" style={{ color: s.accent }}>
                  {s.code}
                </span>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                  {s.tag}
                </div>
              </div>
              <div className="lg:col-span-4">
                <h3 className="font-display text-2xl font-bold tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">
                  {s.name}
                </h3>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm leading-relaxed text-neutral-600">{s.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.deliverables.map((d) => (
                    <span
                      key={d}
                      className="border border-neutral-200 px-2.5 py-1 font-mono text-[10px] tracking-wider text-neutral-500"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex justify-start lg:col-span-2 lg:justify-end">
                <button
                  type="button"
                  data-testid={`service-preview-${s.code.toLowerCase()}`}
                  onClick={() => window.__lenis?.scrollTo("#contatti", { duration: 1.2 })}
                  aria-label={`Richiedi ${s.name} dal form contatti`}
                  className="group/img relative block w-full max-w-[280px] overflow-hidden border border-neutral-200 transition-colors duration-500 hover:border-ink"
                >
                  <img
                    src={SERVICE_IMAGES[s.code].src}
                    alt={SERVICE_IMAGES[s.code].alt}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover/img:scale-105"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-ink/85 px-2 py-1.5 text-center font-mono text-[9px] tracking-[0.15em] text-paper opacity-0 transition-opacity duration-300 group-hover/img:opacity-100">
                    RICHIEDI QUESTO →
                  </span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
